<template>
  <div>
    <app-share-drawer :model-value="paneIsOpen" :title="$t('common.share')"
                      data-locator="bibles-share-pane"
                      :loading="loading" :confirm-disabled="shareDisabled"
                      @close="closePane" @confirm="preparePostBibleRights"
    >
      <template #body>
        <v-row>
          <v-col>
            <DocumentSharePicker v-if="paneIsOpen && !bibleRightsPending"
                                 :force-pm-selection="false"
                                 users-only
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <VisualTextEditor
              v-model:value="customMessage"
              ref="customMessageEditor"
              counter="footer"
              :error-messages="customMessageErrors"
              :max-length="customMessageMaxLength"
              data-locator="optional-message-editor"
              :placeholder="$t('common.optionalMessageWithCopy')"
            />
          </v-col>
        </v-row>
      </template>
    </app-share-drawer>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import { toast } from '@/common/utils/toast'
import { surveyCanBeTriggered, SurveyId, triggerSurvey } from '@/plugins/satismeter'
import { BiblesActionTypes } from '@/store/modules/bibles/action_types'

import DocumentSharePicker from '../../common/document-share/DocumentSharePicker.vue'
import { escapeHtml } from '../../common/utils/strings'
import VisualTextEditor from '../../common/visual-text-editor/VisualTextEditor.vue'
import { DocumentSharePickerActionTypes } from '../../store/modules/document-share-picker/action_types'

const CUSTOM_MESSAGE_MAX_LENGTH = 400

export default {
  name: 'BiblesSharePane',
  emits: ['close-share-pane','share-success'],
  components: { VisualTextEditor, DocumentSharePicker },
  props: {
    bible: {
      type: Object,
    },
    mnemo: {
      type: String,
      required: true,
    },
    paneIsOpen: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      customMessage: '',
      customMessageMaxLength: CUSTOM_MESSAGE_MAX_LENGTH,
      loading: false,
    }
  },
  computed: {
    ...mapState('documentSharePicker', ['selectedMembers']),
    ...mapState('groups', ['groups']),
    ...mapState('bibles', ['biblePendingIds']),
    ...mapState('user', ['profile']),
    customMessageLength () {
      return escapeHtml(this.customMessage).length
    },
    customMessageIsTooLong () {
      return this.customMessageLength > this.customMessageMaxLength
    },
    customMessageErrors () {
      const errors = []

      if (this.customMessageIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    shareDisabled () {
      return this.customMessageIsTooLong
    },
    bibleRightsPending () {
      return this.biblePendingIds.includes(this.bible.id)
    },
  },
  watch: {
    /**
     * Prepares the pane every time it opens. The component used to do this from its own trigger
     * button, which moved to the parent during the Vue 3 migration, leaving the initialisation
     * unreachable: the bible's current rights were no longer fetched, so members who already
     * have read access were no longer disabled in the picker.
     */
    paneIsOpen (isOpen) {
      if (!isOpen) return

      this.INIT_SELECTED_GROUPS([])
      this.INIT_SELECTED_MEMBERS([])
      this.initBibleRights()
    },
  },
  created () {
    this.GET_GROUPS(this.mnemo)
  },
  methods: {
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
      DocumentSharePickerActionTypes.DISABLE_MEMBERS,
    ]),
    ...mapActions('bibles', [BiblesActionTypes.POST_BIBLE_RIGHTS, BiblesActionTypes.GET_BIBLE]),
    ...mapActions('groups', ['GET_GROUPS']),
    async initBibleRights () {
      try {
        const response = await this.GET_BIBLE({
          mnemo: this.mnemo,
          bibleId: this.bible.id,
        })

        if (response.data.rights?.read?.users?.length > 0 && this.groups.length > 0) {
          const membersToDisable = this.groups.flatMap(group => group.members)
            .filter(member => response.data.rights.read.users.some(o => o === member.id))

          if (membersToDisable.length) {
            this.DISABLE_MEMBERS(membersToDisable)
          }
        }
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.bibles.BiblesSharePane.getBibleRightsError'))
      }
    },
    closePane () {
      this.$emit('close-share-pane');
      this.customMessage = ''
      this.$refs.customMessageEditor?.clear()
      this.loading = false
    },
    async preparePostBibleRights () {
      try {
        this.loading = true
        await this.POST_BIBLE_RIGHTS({
          mnemo: this.mnemo,
          bibleId: this.bible.id,
          data: {
            withUsers: this.selectedMembers.map(o => o.id),
            notifyMessage: this.customMessage,
          },
        })
        toast.success(this.$t('project.bibles.BiblesSharePane.postBibleRightsSuccess'))
        this.$emit('share-success')

        const { id: userId, fullName, email, isSubUser } = this.profile
        if (surveyCanBeTriggered(SurveyId.CSAT, userId)) {
          triggerSurvey(SurveyId.CSAT, userId, fullName, email, isSubUser)
        }
        if (surveyCanBeTriggered(SurveyId.NPS, userId)) {
          triggerSurvey(SurveyId.NPS, userId, fullName, email, isSubUser)
        }
      } catch (e) {
        toast.error(this.$t('project.bibles.BiblesSharePane.postBibleRightsError'))
      } finally {
        this.closePane()
      }
    },
  },
}
</script>
